import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getClientOrderSettings } from 'common/services/OrderService';
import { getActiveClientPropertyOptions } from 'admin/services/AdminPropertiesService';

import {
    ORDER_FULFILLMENT_TYPE,
    ORDER_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    DisplayType,
    DocumentContact,
    FulfillmentDetails,
    GenericRecord,
    Instructions,
    Order,
} from 'common/types';
import { FulfillmentDistributionCenter } from 'common/types/trucks';
import { initContact } from 'admin/utils/contact-utils/contact-utils';
import {
    ContactDraftState,
    ContactIdentity,
    createContactDraftReducers,
    isSameContact,
} from 'admin/stores/redux/contactSliceHelpers/contactSliceHelpers';

const newDraftContact = () => initContact();

// Stable identity: a fresh [] here re-renders every subscriber on
// every dispatch.
const EMPTY_CONTACTS: DocumentContact[] = [];

interface CloneFormData {
    clientId: number;
    opsJobManager?: number;
    priorityId?: string;
    extPoNumber?: string;
    extReferenceNumber1?: string;
    extReferenceNumber2?: string;
    instructions: Instructions;
    propertyId?: number | null;
    contacts: DocumentContact[];
    fulfillmentDetails: FulfillmentDetails;
    vendorDistributionCenter?: FulfillmentDistributionCenter | null;
    hasFulfillmentMethods?: boolean;
    fulfillmentMethod?: {
        id: string;
        fulfillmentTypeId: ORDER_FULFILLMENT_TYPE;
        display: string;
        hasFreight: boolean;
        billingThreshold: number;
    } | null;
    propertyRequired?: boolean;
}

export interface State extends ContactDraftState {
    initialized: boolean;
    formData: CloneFormData;
    propertyRequired: boolean;
    originalClientId: number;
    isWorkOrderProducing: boolean;
    clientProperties: DisplayType<number>[];
    clientPropertiesLoading: boolean;
    areFulfillmentDetailsComplete: boolean;
    pickupFulfillmentDetails: Partial<FulfillmentDetails>;
    deliveryFulfillmentDetails: Partial<FulfillmentDetails>;
}

const initialFormData: CloneFormData = {
    clientId: 0,
    opsJobManager: 0,
    priorityId: '',
    extPoNumber: '',
    extReferenceNumber1: '',
    extReferenceNumber2: '',
    instructions: {},
    propertyId: 0,
    contacts: [],
    fulfillmentDetails: {},
    vendorDistributionCenter: null,
    hasFulfillmentMethods: false,
    fulfillmentMethod: null,
    propertyRequired: false,
};

const initialState: State = {
    initialized: false,
    formData: initialFormData,
    draftContact: newDraftContact(),
    pickupFulfillmentDetails: {},
    propertyRequired: false,
    originalClientId: 0,
    isWorkOrderProducing: false,
    clientProperties: [],
    clientPropertiesLoading: false,
    areFulfillmentDetailsComplete: false,
    deliveryFulfillmentDetails: {},
};

function computeAreFulfillmentDetailsComplete(
    fulfillmentDetails: Partial<FulfillmentDetails> | undefined
): boolean {
    const {
        fulfillmentTypeId,
        fulfillmentToAddress,
        fulfillmentDistributionCenterId,
    } = fulfillmentDetails ?? {};

    switch (fulfillmentTypeId) {
        case ORDER_FULFILLMENT_TYPE.PICK_UP:
            return !!fulfillmentDistributionCenterId;
        case ORDER_FULFILLMENT_TYPE.DELIVERY:
            return !!fulfillmentToAddress;
        case ORDER_FULFILLMENT_TYPE.CONTRACTOR_SUPPLIED:
            return true;
        default:
            return false;
    }
}

export const updateClient = createAsyncThunk(
    'orderClone/updateClient',
    async ({
        clientId,
        currentClientId,
        orderContacts,
    }: {
        clientId: number;
        currentClientId: number;
        orderContacts: DocumentContact[];
    }) => {
        if (clientId !== currentClientId) {
            const { hasFulfillmentMethods, propertyRequired } =
                await getClientOrderSettings(clientId);

            return {
                clientId,
                hasFulfillmentMethods,
                propertyRequired,
                orderContacts,
            };
        }

        return null;
    }
);

export const loadClientProperties = createAsyncThunk(
    'orderClone/loadClientProperties',
    (clientId: number): Promise<DisplayType<number>[]> =>
        getActiveClientPropertyOptions(clientId)
);

function saveSlot(state: State, type: ORDER_FULFILLMENT_TYPE) {
    if (type === ORDER_FULFILLMENT_TYPE.PICK_UP) {
        state.pickupFulfillmentDetails = {
            ...state.formData.fulfillmentDetails,
        };
    } else if (type === ORDER_FULFILLMENT_TYPE.DELIVERY) {
        state.deliveryFulfillmentDetails = {
            ...state.formData.fulfillmentDetails,
        };
    }
}

const slotForType = (state: State, type: ORDER_FULFILLMENT_TYPE) =>
    type === ORDER_FULFILLMENT_TYPE.PICK_UP
        ? state.pickupFulfillmentDetails
        : state.deliveryFulfillmentDetails;

function applyFulfillmentUpdate(
    state: State,
    fulfillmentDetails: Partial<FulfillmentDetails>
) {
    const current = state.formData.fulfillmentDetails;
    const currentType =
        current?.fulfillmentTypeId ?? (current?.type as ORDER_FULFILLMENT_TYPE);
    const newType = fulfillmentDetails.fulfillmentTypeId ?? currentType;

    if (newType && newType !== currentType) {
        saveSlot(state, currentType);
        const restoredSlot = slotForType(state, newType);
        state.formData.fulfillmentDetails = {
            ...restoredSlot,
            fulfillmentTypeId: newType,
            typeDisplay:
                fulfillmentDetails.typeDisplay ?? restoredSlot?.typeDisplay,
        };
    } else {
        state.formData.fulfillmentDetails = {
            ...current,
            ...fulfillmentDetails,
        };
        saveSlot(state, newType);
    }
}

export const adminOrderClone = createSlice({
    name: 'adminOrderClone',
    initialState,
    reducers: {
        initialize: (state, action: PayloadAction<Order>) => {
            const order = action.payload;

            const fulfillmentDetails: FulfillmentDetails = {
                fulfillmentTypeId: order?.fulfillmentTypeId,
                fulfillmentDistributionCenterId:
                    order?.fulfillmentDistributionCenterId || undefined,
                fulfillmentTo: order?.fulfillmentTo,
                fulfillmentToAddress: order?.fulfillmentToAddress,
                fulfillmentToCity: order?.fulfillmentToCity,
                fulfillmentToRegion: order?.fulfillmentToRegion,
                fulfillmentToPostalCode: order?.fulfillmentToPostalCode,
                fulfillmentToCountry: order?.fulfillmentToStdCountry,
                fulfillmentToStdCountry: order?.fulfillmentToStdCountry,
                residentialDelivery: order?.residentialDelivery,
            };

            state.initialized = true;
            state.formData = {
                clientId: order.clientId,
                opsJobManager: order.opsJobManager,
                priorityId: order.priorityId,
                extPoNumber: order.extPoNumber,
                extReferenceNumber1: order.extReferenceNumber1,
                extReferenceNumber2: order.extReferenceNumber2,
                instructions: order.instructions ?? {},
                contacts: order.contacts ?? [],
                fulfillmentDetails,
                vendorDistributionCenter: null,
                hasFulfillmentMethods: order.hasFulfillmentMethods,
                fulfillmentMethod: order.fulfillmentMethod,
                propertyId: order.propertyId,
            };
            state.propertyRequired = order.propertyRequired ?? false;
            state.originalClientId = order.clientId;
            state.isWorkOrderProducing =
                order.orderTypeId === ORDER_TYPE.ORDER_WITH_ORIGIN;
            state.areFulfillmentDetailsComplete =
                computeAreFulfillmentDetailsComplete(fulfillmentDetails);

            if (order.fulfillmentTypeId === ORDER_FULFILLMENT_TYPE.PICK_UP) {
                state.pickupFulfillmentDetails = fulfillmentDetails;
            } else if (
                order.fulfillmentTypeId === ORDER_FULFILLMENT_TYPE.DELIVERY
            ) {
                state.deliveryFulfillmentDetails = fulfillmentDetails;
            }
        },
        reset: () => ({ ...initialState, draftContact: newDraftContact() }),

        updateKeyValuePair: (
            state,
            action: PayloadAction<{ key: string | string[]; value: unknown }>
        ) => {
            if (!state.initialized) {
                return;
            }

            const { key, value } = action.payload;
            const update: GenericRecord<unknown> = {};

            if (Array.isArray(key)) {
                key.forEach((k, idx) => {
                    update[k] = (value as unknown[])[idx];
                });
            } else {
                update[key] = value;
            }

            state.formData = { ...state.formData, ...update };
        },
        updateProperty: (state: State, action: PayloadAction<number>) => {
            if (!state.initialized) {
                return;
            }

            state.formData.propertyId = action.payload;
        },
        updateSpecialInstructions: (
            state: State,
            action: PayloadAction<string>
        ) => {
            if (!state.initialized) {
                return;
            }

            state.formData.instructions = {
                ...state.formData.instructions,
                special: action.payload,
            };
        },
        addContact: (state: State, action: PayloadAction<DocumentContact>) => {
            if (!state.initialized) {
                return;
            }

            const alreadyExists = state.formData.contacts?.some(
                c => c.uuid === action.payload.uuid
            );
            if (alreadyExists) return;

            state.formData.contacts = [
                ...(state.formData.contacts ?? []),
                action.payload,
            ];
        },
        removeContact: (
            state: State,
            action: PayloadAction<ContactIdentity>
        ) => {
            if (!state.initialized) {
                return;
            }

            state.formData.contacts = state.formData.contacts?.filter(
                contact => !isSameContact(contact, action.payload)
            );
        },
        ...createContactDraftReducers<State>(newDraftContact),
        updateFulfillmentDetails: (
            state: State,
            action: PayloadAction<{
                fulfillmentDetails?: Partial<FulfillmentDetails>;
                fulfillmentDistributionCenter?: FulfillmentDistributionCenter | null;
            }>
        ) => {
            if (!state.initialized) {
                return;
            }

            const { fulfillmentDetails: fd, fulfillmentDistributionCenter } =
                action.payload;

            if (fd !== undefined) {
                applyFulfillmentUpdate(state, {
                    fulfillmentTo: fd.fulfillmentTo,
                    fulfillmentToAddress: fd.fulfillmentToAddress,
                    fulfillmentToCity: fd.fulfillmentToCity,
                    fulfillmentToRegion: fd.fulfillmentToRegion,
                    fulfillmentToPostalCode: fd.fulfillmentToPostalCode,
                    fulfillmentToCountry: fd.fulfillmentToCountry,
                    fulfillmentTypeId: fd.fulfillmentTypeId,
                    typeDisplay: fd.typeDisplay,
                    residentialDelivery: fd.residentialDelivery,
                    fulfillmentDistributionCenterId:
                        fd.fulfillmentDistributionCenterId,
                });
            }

            if (fulfillmentDistributionCenter !== undefined) {
                state.formData.vendorDistributionCenter =
                    fulfillmentDistributionCenter;
            }

            state.areFulfillmentDetailsComplete =
                computeAreFulfillmentDetailsComplete(
                    state.formData.fulfillmentDetails
                );
        },
    },
    extraReducers: builder => {
        builder.addCase(updateClient.fulfilled, (state, action) => {
            if (!state.initialized || !action.payload) {
                return;
            }

            const {
                clientId,
                hasFulfillmentMethods,
                propertyRequired,
                orderContacts,
            } = action.payload;

            const fulfillmentMethod = hasFulfillmentMethods
                ? state.formData.fulfillmentMethod
                : null;

            const clientChanged = clientId !== state.originalClientId;
            state.propertyRequired =
                propertyRequired ||
                (clientChanged && state.isWorkOrderProducing);
            state.formData = {
                ...state.formData,
                clientId,
                hasFulfillmentMethods,
                fulfillmentMethod,
                propertyId: clientChanged ? null : state.formData.propertyId,
                contacts: clientChanged ? [] : orderContacts,
            };
        });
        builder.addCase(loadClientProperties.pending, state => {
            state.clientProperties = [];
            state.clientPropertiesLoading = true;
        });
        builder.addCase(loadClientProperties.fulfilled, (state, action) => {
            state.clientProperties = action.payload;
            state.clientPropertiesLoading = false;
        });
        builder.addCase(loadClientProperties.rejected, state => {
            state.clientProperties = [];
            state.clientPropertiesLoading = false;
        });
    },
});

export const {
    initialize,
    reset,
    updateKeyValuePair,
    addContact,
    removeContact,
    setDraftContact,
    resetDraftContact,
    updateSpecialInstructions,
    updateProperty,
    updateFulfillmentDetails,
} = adminOrderClone.actions;

interface StateSlice {
    adminOrderClone: State;
}

export const orderCloneSelectedContacts = (state: StateSlice) =>
    state.adminOrderClone.formData.contacts ?? EMPTY_CONTACTS;

export const orderCloneDraftContact = (state: StateSlice) =>
    state.adminOrderClone.draftContact;

export default adminOrderClone.reducer;
